﻿
export class GeminiAudioInput {
    static inputAudioContext: AudioContext;
    static workletNode: AudioWorkletNode | null;
    static sourceNode: MediaStreamAudioSourceNode | null;
    static mediaStream: MediaStream | null;
    static Muted = false;
    static Capturing = false;

    static async Capture(callback: any) {
        if (GeminiAudioInput.Capturing)
            GeminiAudioInput.Stop();

        try {
            // Let the browser pick the device sample rate (usually 48000) for best AEC behavior.
            GeminiAudioInput.inputAudioContext = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
            await GeminiAudioInput.inputAudioContext.resume();

            const supported = navigator.mediaDevices.getSupportedConstraints?.() ?? {};
            const audioConstraints: MediaTrackConstraints = {
                echoCancellation: supported.echoCancellation ? { ideal: true } as any : undefined,
                noiseSuppression: supported.noiseSuppression ? { ideal: true } as any : undefined,
                autoGainControl: supported.autoGainControl ? { ideal: true } as any : undefined,
                channelCount: { ideal: 1 },
            };
            
            console.log("Audio constraints:", audioConstraints);

            GeminiAudioInput.mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: audioConstraints,
                video: false
            });

            // Re-apply constraints (some browsers only honor applyConstraints).
            const track = GeminiAudioInput.mediaStream.getAudioTracks()[0];
            if (track) {
                try { await track.applyConstraints(audioConstraints); } catch { }
                console.log("Mic track settings:", track.getSettings());
            }

            GeminiAudioInput.sourceNode = GeminiAudioInput.inputAudioContext.createMediaStreamSource(GeminiAudioInput.mediaStream);

            GeminiAudioInput.workletNode = null;
            if ((GeminiAudioInput.inputAudioContext as any).audioWorklet?.addModule) {
                try {
                    // NOTE: add resampling to 16k inside the worklet (see below).
                    const workletCode = `
class MicCaptureProcessor extends AudioWorkletProcessor{
  constructor(){
    super();
    this._inRate = sampleRate;
    this._outRate = 16000;
    this._ratio = this._inRate / this._outRate;
    this._pos = 0;
    this._last = 0;
  }
  process(inputs){
    const input = inputs[0];
    if (!input || !input[0]) return true;
    const ch0 = input[0];

    // Linear resample to 16k (good enough for voice, avoids changing device rate).
    const outLen = Math.floor((ch0.length + (this._ratio - this._pos)) / this._ratio);
    const out = new Float32Array(outLen);
    let o = 0;

    while (o < outLen) {
      const idx = this._pos;
      const i0 = Math.floor(idx);
      const frac = idx - i0;

      const s0 = (i0 >= 0 && i0 < ch0.length) ? ch0[i0] : this._last;
      const s1 = (i0 + 1 >= 0 && i0 + 1 < ch0.length) ? ch0[i0 + 1] : s0;
      out[o++] = s0 + (s1 - s0) * frac;

      this._pos += this._ratio;
    }

    // Keep position within current buffer and remember last sample.
    this._pos -= ch0.length;
    this._last = ch0[ch0.length - 1];

    this.port.postMessage(out, [out.buffer]);
    return true;
  }
}
registerProcessor('mic-capture-processor', MicCaptureProcessor);
`;
                    const blob = new Blob([workletCode], { type: "application/javascript" });
                    const url = URL.createObjectURL(blob);
                    await (GeminiAudioInput.inputAudioContext as any).audioWorklet.addModule(url);
                    URL.revokeObjectURL(url);

                    GeminiAudioInput.workletNode = new AudioWorkletNode(
                        GeminiAudioInput.inputAudioContext,
                        "mic-capture-processor",
                        { numberOfInputs: 1, numberOfOutputs: 0, channelCount: 1 }
                    );

                    GeminiAudioInput.workletNode.port.onmessage = (evt: MessageEvent) => {
                        const float32 = evt.data as Float32Array; // already 16k now
                        const pcm16 = GeminiAudioInput.floatToInt16PCM(float32);
                        const b64 = GeminiAudioInput.createPcm16Base64(pcm16);

                        if (!GeminiAudioInput.Muted)
                            callback(b64, "audio/pcm;rate=16000");
                    };

                    GeminiAudioInput.sourceNode.connect(GeminiAudioInput.workletNode);
                } catch (err) {
                    console.error("AudioWorklet not available or failed", err);
                    GeminiAudioInput.Stop();
                    return false;
                }
            } else {
                console.error("AudioWorklet not available or failed");
                GeminiAudioInput.Stop();
                return false;
            }

            GeminiAudioInput.Capturing = true;
            GeminiAudioInput.Muted = false;
            return true;
        } catch (e) {
            console.error("Error starting microphone", e);
            GeminiAudioInput.Stop();
            return false;
        }
    }

    static Mute(muted: boolean) {
        GeminiAudioInput.Muted = muted;        
    }
    
    static Stop() {
        if (!GeminiAudioInput.Capturing) return;
        GeminiAudioInput.Capturing = false;
        
        if (GeminiAudioInput.inputAudioContext){
            try{ GeminiAudioInput.inputAudioContext.close().catch(); } catch(error) {
                console.error("Failed to close microphone context", error);
            }
        }
        
        if (GeminiAudioInput.workletNode) {
            try { GeminiAudioInput.workletNode?.disconnect(); } catch (error) {
                console.error("Failed to disconnect microphone worklet", error);
            }
            GeminiAudioInput.workletNode = null;
        }
        if (GeminiAudioInput.sourceNode) {
            try { GeminiAudioInput.sourceNode.disconnect(); } catch(error) {
                console.error("Failed to disconnect microphone source", error);
            }
            GeminiAudioInput.sourceNode = null;
        }
        if (GeminiAudioInput.mediaStream) {
            try { GeminiAudioInput.mediaStream.getTracks().forEach((t) => t.stop()); } catch(error) {
                console.error("Failed to stop microphone stream", error);
            }
            GeminiAudioInput.mediaStream = null; 
        }
    }


    static floatToInt16PCM(float32: Float32Array): Int16Array {
        const out = new Int16Array(float32.length);
        for (let i = 0; i < float32.length; i++) {
            const s = Math.max(-1, Math.min(1, float32[i]));
            out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        return out;
    }

    // Prefer WAV for broad compatibility: some Gemini Live configurations reject raw PCM chunks.
    static createPcm16Base64(pcm16: Int16Array): string {
        // Convert PCM16 samples to base64.
        // We copy to a Uint8Array to avoid SharedArrayBuffer typing issues in some environments.
        const bytes = new Uint8Array(pcm16.byteLength);
        bytes.set(new Uint8Array(pcm16.buffer, pcm16.byteOffset, pcm16.byteLength));

        let binary = "";
        // Chunk to avoid call stack / argument limits.
        const chunkSize = 0x8000;
        for (let i = 0; i < bytes.length; i += chunkSize) {
            const chunk = bytes.subarray(i, i + chunkSize);
            binary += String.fromCharCode(...chunk);
        }

        return btoa(binary);
    }
}
